import { Box, Flex, Heading, RadioProps, Skeleton, Tag, TagLabel, Text, useRadio } from '@chakra-ui/react';

import { accreditationColorHex, AccreditationColorScheme } from '~/features/accreditation/types';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';

interface SubscriptionOptionProps extends RadioProps {
  ariaLabel: string;
  colorScheme?: AccreditationColorScheme;
  label: string;
  price: string | undefined;
  strikeThroughPrice: string | null | undefined;
  couponPercentage: number;
}

function getSavingsBlurb(value: BillingFrequency, couponPercentage: number) {
  if (couponPercentage > 0) {
    return `${couponPercentage}% off`;
  }
  if (value === billingFrequencySchema.enum['4month']) {
    return 'SAVE 15%';
  }
  if (value === billingFrequencySchema.enum['annually']) {
    return 'SAVE 20%';
  }
  return 'FLEXIBLE';
}

export function SubscriptionOption({
  ariaLabel,
  colorScheme = 'blue',
  label,
  price,
  strikeThroughPrice,
  couponPercentage,
  ...props
}: SubscriptionOptionProps) {
  const { state, getInputProps, getRadioProps } = useRadio(props);
  const input = getInputProps();
  const radio = getRadioProps();
  const value = props.value as BillingFrequency;
  const isSelected = state.isChecked;
  const savingsBlurb = getSavingsBlurb(value, couponPercentage);

  return (
    <Box as='label' cursor='pointer' width='100%'>
      <input {...input} aria-label={ariaLabel} />
      <Box
        {...radio}
        alignItems='center'
        bg={isSelected ? `${colorScheme}.50` : 'grey.50'}
        border='1.33px solid'
        borderColor={isSelected ? `${colorScheme}.500` : 'grey.100'}
        borderRadius='4px'
        display='flex'
        gap='12px'
        p='10px 12px'
        _focus={{
          boxShadow: 'outline',
        }}
        _hover={{
          borderColor: isSelected ? `${colorScheme}.500` : 'grey.100',
        }}
      >
        <Box alignSelf='normal' position='relative'>
          {isSelected ? (
            <svg fill='none' height='19' viewBox='0 0 20 20' width='18' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10Z'
                fill={accreditationColorHex[colorScheme][500]}
              />
              <path
                d='M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z'
                fill='white'
              />
            </svg>
          ) : (
            <svg fill='none' height='19' viewBox='0 0 20 20' width='18' xmlns='http://www.w3.org/2000/svg'>
              <rect fill='#F2F2F2' height='18.67' rx='9.335' width='18.67' x='0.665' y='0.665' />
              <rect height='18.67' rx='9.335' stroke='#9C9FA8' strokeWidth='1.33' width='18.67' x='0.665' y='0.665' />
            </svg>
          )}
        </Box>
        <Flex flex={1} flexDirection='column' justifyContent='space-between'>
          <Flex direction='row' flex={1} gap='4px' justifyContent='space-between'>
            <Heading
              as='p'
              color={isSelected ? 'grey.900' : 'grey.500'}
              fontFamily='condensed'
              fontSize='0.9375rem'
              fontWeight='500'
              letterSpacing='1px'
              lineHeight='1'
              textTransform='uppercase'
            >
              {label}
            </Heading>
            <Tag
              bg={isSelected ? `${colorScheme}.500` : 'grey.500'}
              border='none'
              borderRadius='2px'
              color='white'
              fontFamily='condensed'
              fontSize='14px'
              fontWeight='500'
              letterSpacing='0.06em'
              lineHeight='1'
              textTransform='uppercase'
              whiteSpace='nowrap'
            >
              <TagLabel>{savingsBlurb}</TagLabel>
            </Tag>
          </Flex>
          <Flex alignItems='flex-end' gap='4px'>
            <Heading
              as='p'
              color={isSelected ? 'grey.900' : 'grey.500'}
              fontFamily='body'
              fontSize='1.375rem'
              fontWeight='600'
              lineHeight='1.75rem'
            >
              {price || <Skeleton as='span' display='block' height='30px' width='100px' />}
            </Heading>
            <Text color='grey.500' fontSize='12px'>
              /month
            </Text>

            {strikeThroughPrice && (
              <Heading as='p' color='grey.300' marginLeft='auto' size='m' textDecor='line-through'>
                {strikeThroughPrice.replace('/month', '')}
              </Heading>
            )}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
